<template>
  <router-view />
  <!-- Diálogo global de aviso/confirmación (reemplaza los modal-alerta duplicados). -->
  <ConfirmDialog />
</template>

<script setup>
import { onMounted, onUnmounted, watch } from 'vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import { useTurnosStore } from '@/stores/turnos';
import { useUsuariosStore } from '@/stores/usuarios';
import { useNotificaciones } from '@/composables/useNotificaciones';
import { inicializarInstallPrompt } from '@/composables/useInstallPrompt';

onMounted(() => inicializarInstallPrompt());

const turnosStore = useTurnosStore();
const usuariosStore = useUsuariosStore();
const { iniciarSistemaNotificaciones, notificacionesHabilitadas } = useNotificaciones();

let intervaloNotificaciones = null;
let dataCargada = false;

// Cargar datos solo cuando el usuario esté disponible
watch(
  () => usuariosStore.usuarioActual,
  async (usuario) => {
    if (usuario && !dataCargada) {
      dataCargada = true;
      await turnosStore.cargarDesdeLocalStorage();

      if (notificacionesHabilitadas.value) {
        intervaloNotificaciones = await iniciarSistemaNotificaciones();
      }
    } else if (!usuario) {
      dataCargada = false;
    }
  },
  { immediate: true }
);

// Observar cambios en las notificaciones habilitadas
watch(notificacionesHabilitadas, async (nuevoValor) => {
  if (nuevoValor && !intervaloNotificaciones) {
    // Si se habilitaron y no están corriendo, iniciar
    intervaloNotificaciones = await iniciarSistemaNotificaciones();
  } else if (!nuevoValor && intervaloNotificaciones) {
    // Si se deshabilitaron y están corriendo, detener
    clearInterval(intervaloNotificaciones);
    intervaloNotificaciones = null;
  }
});

onUnmounted(() => {
  // Limpiar intervalo al desmontar
  if (intervaloNotificaciones) {
    clearInterval(intervaloNotificaciones);
  }
});
</script>

<style>
/* 2026-08-05 — Este comentario existe para forzar un hash nuevo de `app.css`.
 *
 * Contexto: durante un deploy, un nodo de Cloudflare respondió el index.html en la URL del CSS
 * (el fallback de SPA con código 200) y el service worker guardó ESE HTML en su precache como si
 * fuera la hoja de estilos. Resultado: la app abre sin estilos, y no se cura sola — workbox solo
 * vuelve a bajar un archivo cuando cambia su entrada en el manifiesto, y el nombre no cambiaba.
 * Purgar el CDN NO alcanza: arregla el origen, no la copia ya guardada en cada navegador.
 *
 * Cambiar cualquier cosa acá renombra el archivo, el service worker lo ve como nuevo y lo baja
 * limpio. Verificado: el comentario NO sobrevive a la minificación (el CSS final no tiene
 * ninguno), pero el hash igual cambia, porque se calcula sobre el fuente.
 *
 * Si vuelve a pasar, tocar este bloque y desplegar. Ver el ritual de deploy en las notas.
 */

/* Estilos globales para eliminar tap-highlight en todos los elementos interactivos */
* {
  -webkit-tap-highlight-color: transparent;
}

button,
a,
[role="button"],
[onclick],
.clickable {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Permitir selección de texto en inputs y áreas de texto */
input,
textarea,
[contenteditable="true"] {
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
}
</style>
